import React, { useMemo } from 'react';
import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { visit } from 'unist-util-visit';
import { CopyButton } from '../ui';
import { highlightParts, escapeRegExp } from '../lib/search';
import type { PhraseSection as PhraseSectionType } from '../lib/types';

export type PhraseSectionProps = {
  section: PhraseSectionType;
  onCopied?: () => void;
  /** Query terms to highlight in the rendered note (from a search deep-link). */
  highlight?: string[];
};

/**
 * Escape stray `<` that don't open a known-safe tag (<a>, </a>, <br>) so the
 * raw-HTML renderer can't swallow placeholder text such as `<Sleep study>` or
 * an unclosed `<gerd sx:...`. Skips fenced code blocks, where `<` is already
 * literal (and HTML entities would otherwise show through verbatim).
 */
function escapeStrayHtml(md: string): string {
  let inFence = false;
  return md
    .split('\n')
    .map((line) => {
      if (/^\s*```/.test(line)) {
        inFence = !inFence;
        return line;
      }
      if (inFence) return line;
      return line.replace(/<(?!\/?(?:a|br)\b)/gi, '&lt;');
    })
    .join('\n');
}

/** Render authored single line breaks (soft breaks) as hard breaks, matching
 *  the prior preformatted display. Fenced code is untouched (it is a `code`
 *  node, not `text`). */
function remarkHardBreaks() {
  return (tree: unknown) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    visit(tree as any, 'text', (node: any, index: number | undefined, parent: any) => {
      if (!parent || index == null || typeof node.value !== 'string' || !node.value.includes('\n')) {
        return;
      }
      const out: any[] = [];
      node.value.split('\n').forEach((part: string, i: number) => {
        if (i > 0) out.push({ type: 'break' });
        out.push({ type: 'text', value: part });
      });
      parent.children.splice(index, 1, ...out);
      return index + out.length;
    });
  };
}

const MD_COMPONENTS: Components = {
  // Links always open in a new tab, safely.
  a({ node, children, ...props }) {
    return (
      <a {...props} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  },
  // Let wide tables scroll horizontally instead of overflowing the card.
  table({ node, ...props }) {
    return (
      <div style={{ overflowX: 'auto' }}>
        <table {...props} />
      </div>
    );
  },
};

/** Wrap query-term matches in plain prose / list / table text with <mark> nodes.
 *  Fenced clinical blocks are mdast `code` nodes (no child text nodes), so they
 *  are handled separately by the `code` component below. */
function remarkHighlight(terms: string[]) {
  const cleaned = terms.filter(Boolean).map(escapeRegExp);
  const re = cleaned.length ? new RegExp(`(${cleaned.join('|')})`, 'gi') : null;
  return function () {
    return (tree: unknown) => {
      if (!re) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      visit(tree as any, 'text', (node: any, index: number | undefined, parent: any) => {
        if (!parent || index == null || typeof node.value !== 'string') return;
        re.lastIndex = 0;
        if (!re.test(node.value)) return;
        re.lastIndex = 0;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const out: any[] = [];
        let last = 0;
        let m: RegExpExecArray | null;
        while ((m = re.exec(node.value)) !== null) {
          if (m.index > last) out.push({ type: 'text', value: node.value.slice(last, m.index) });
          out.push({
            type: 'highlight',
            data: { hName: 'mark', hProperties: { className: 'dp-hl' } },
            children: [{ type: 'text', value: m[0] }],
          });
          last = m.index + m[0].length;
          if (m[0].length === 0) re.lastIndex++;
        }
        if (last < node.value.length) out.push({ type: 'text', value: node.value.slice(last) });
        parent.children.splice(index, 1, ...out);
        return index + out.length;
      });
    };
  };
}

const HL_STYLE: React.CSSProperties = {
  background: 'var(--accent-fill)', color: 'var(--accent)', borderRadius: 3, padding: '0 1px',
};

function renderHighlighted(text: string, terms: string[]) {
  return highlightParts(text, terms).map((p, i) =>
    p.hit ? (
      <mark key={i} className="dp-hl" style={HL_STYLE}>{p.text}</mark>
    ) : (
      <React.Fragment key={i}>{p.text}</React.Fragment>
    )
  );
}

/** Components with term highlighting added — used only when rendering a search hit. */
function makeHighlightComponents(terms: string[]): Components {
  return {
    ...MD_COMPONENTS,
    mark({ children }) {
      return <mark className="dp-hl" style={HL_STYLE}>{children}</mark>;
    },
    code({ node, children, ...props }) {
      if (typeof children !== 'string') return <code {...props}>{children}</code>;
      return <code {...props}>{renderHighlighted(children, terms)}</code>;
    },
  };
}

const REMARK_PLUGINS = [remarkGfm, remarkHardBreaks];
const REHYPE_PLUGINS = [rehypeRaw];
const NO_TERMS: string[] = [];

export function PhraseSection({ section, onCopied, highlight }: PhraseSectionProps) {
  const markdown = useMemo(
    () => escapeStrayHtml(section.raw ?? section.body),
    [section.raw, section.body]
  );

  const terms = highlight ?? NO_TERMS;
  const hasHighlight = terms.length > 0;
  const remarkPlugins = useMemo(
    () => (hasHighlight ? [remarkGfm, remarkHardBreaks, remarkHighlight(terms)] : REMARK_PLUGINS),
    [hasHighlight, terms]
  );
  const components = useMemo(
    () => (hasHighlight ? makeHighlightComponents(terms) : MD_COMPONENTS),
    [hasHighlight, terms]
  );

  return (
    <section
      style={{
        background: 'var(--bg-surface)',
        borderRadius: 'var(--r-lg)',
        border: '1px solid var(--line-divider)',
        overflow: 'hidden',
      }}
    >
      <header
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '1px solid var(--line-divider)',
          background: 'var(--bg-grouped)',
        }}
      >
        <h3
          style={{
            margin: 0, fontSize: 14, fontWeight: 600,
            color: 'var(--accent)', letterSpacing: '-0.16px',
          }}
        >
          {section.title}
        </h3>
        {/* Copy still yields the clean, EHR-pasteable plain text. */}
        <CopyButton text={section.body} onCopied={onCopied} />
      </header>
      <div className="dp-markdown" style={{ padding: '16px 18px' }}>
        <Markdown
          remarkPlugins={remarkPlugins}
          rehypePlugins={REHYPE_PLUGINS}
          components={components}
        >
          {markdown}
        </Markdown>
      </div>
    </section>
  );
}

export default PhraseSection;
